// src/LicenseHistory.js
// Admin-only License History tracking component

import React, { useState, useEffect, useContext } from 'react';
import axios from './axiosInstance';
import { AuthContext } from './context/AuthContext';
import './styles/LicenseHistory.css';

const LicenseHistory = () => {
  const { user } = useContext(AuthContext);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    licenseId: '',
    startDate: '',
    endDate: '',
    limit: 100,
    skip: 0
  });

  // Pagination
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  // Selected entry for detail view
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    // Allow both super admins and regular admins to view history
    if (user && (user.isSuperAdmin || user.role === 'admin')) {
      fetchHistory();
      fetchStats();
    }
  }, [user, filters]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });

      const res = await axios.get(`/license-history?${params.toString()}`);
      setHistory(res.data.history || []);
      setTotal(res.data.total || 0);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError('Failed to load license history');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/license-history/stats');
      setStats(res.data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      skip: 0 // Reset pagination when filters change
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setFilters(prev => ({
      ...prev,
      skip: (newPage - 1) * itemsPerPage
    }));
  };

  const handleExport = async () => {
    try {
      const res = await axios.post('/license-history/export', { filters }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `license-history-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting history:', err);
      alert('Failed to export history');
    }
  };

  const viewDetails = (entry) => {
    setSelectedEntry(entry);
    setShowDetailModal(true);
  };

  const getActionBadgeClass = (action) => {
    const classMap = {
      'generated': 'badge-success',
      'updated': 'badge-info',
      'replied_to_ticket': 'badge-primary',
      'sharepoint_posted': 'badge-warning',
      'invoice_uploaded': 'badge-secondary',
      'ticket_closed': 'badge-dark'
    };
    return `action-badge ${classMap[action] || 'badge-default'}`;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionDisplayName = (action) => {
    const nameMap = {
      'generated': 'License Generated',
      'updated': 'License Updated',
      'replied_to_ticket': 'Replied to Ticket',
      'sharepoint_posted': 'Posted to SharePoint',
      'invoice_uploaded': 'Invoice Uploaded',
      'ticket_closed': 'Ticket Closed'
    };
    return nameMap[action] || action;
  };

  // Check if user is admin (super admin or regular admin)
  if (!user || (!user.isSuperAdmin && user.role !== 'admin')) {
    return (
      <div className="license-history-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only Administrators can view license history.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="license-history-container">
      <div className="history-header">
        <h2>License Management History</h2>
        <p className="subtitle">Complete audit trail of all license actions</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-cards">
          <div className="stat-card">
            <h3>{stats.totalActions.toLocaleString()}</h3>
            <p>Total Actions</p>
          </div>
          {stats.actionBreakdown && stats.actionBreakdown.slice(0, 3).map(item => (
            <div className="stat-card" key={item._id}>
              <h3>{item.count.toLocaleString()}</h3>
              <p>{getActionDisplayName(item._id)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="history-filters">
        <div className="filter-group">
          <label>Action Type</label>
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
          >
            <option value="">All Actions</option>
            <option value="generated">License Generated</option>
            <option value="updated">License Updated</option>
            <option value="replied_to_ticket">Replied to Ticket</option>
            <option value="sharepoint_posted">Posted to SharePoint</option>
            <option value="invoice_uploaded">Invoice Uploaded</option>
            <option value="ticket_closed">Ticket Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label>License ID</label>
          <input
            type="text"
            placeholder="Search by License ID..."
            value={filters.licenseId}
            onChange={(e) => handleFilterChange('licenseId', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Start Date</label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>End Date</label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button
            onClick={() => {
              setFilters({
                action: '',
                licenseId: '',
                startDate: '',
                endDate: '',
                limit: 100,
                skip: 0
              });
              setCurrentPage(1);
            }}
            className="btn-clear-filters"
          >
            Clear Filters
          </button>
          <button onClick={handleExport} className="btn-export">
            Export to CSV
          </button>
        </div>
      </div>

      {/* History Table */}
      {loading ? (
        <div className="loading-spinner">Loading history...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : history.length === 0 ? (
        <div className="no-data">No history entries found</div>
      ) : (
        <>
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action</th>
                  <th>Performed By</th>
                  <th>License ID</th>
                  <th>Company</th>
                  <th>Ticket ID</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry, index) => (
                  <tr key={entry._id || index}>
                    <td className="timestamp-cell">
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td>
                      <span className={getActionBadgeClass(entry.action)}>
                        {getActionDisplayName(entry.action)}
                      </span>
                    </td>
                    <td className="user-cell">
                      <div className="user-info">
                        <strong>{entry.performedBy.displayName}</strong>
                        <small>{entry.performedBy.email}</small>
                      </div>
                    </td>
                    <td className="license-id-cell">
                      {entry.license?.licenseId || '-'}
                    </td>
                    <td>{entry.license?.company || '-'}</td>
                    <td className="ticket-cell">
                      {entry.redmineTicket?.ticketId ? (
                        <a
                          href={`https://redmine.solumesl.com/issues/${entry.redmineTicket.ticketId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          #{entry.redmineTicket.ticketId}
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      <button
                        onClick={() => viewDetails(entry)}
                        className="btn-view-details"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-page"
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages} ({total.toLocaleString()} total entries)
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn-page"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedEntry && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>History Entry Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h4>Action Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Action:</label>
                    <span className={getActionBadgeClass(selectedEntry.action)}>
                      {getActionDisplayName(selectedEntry.action)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Timestamp:</label>
                    <span>{formatTimestamp(selectedEntry.timestamp)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Performed By</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name:</label>
                    <span>{selectedEntry.performedBy.displayName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedEntry.performedBy.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Username:</label>
                    <span>{selectedEntry.performedBy.username}</span>
                  </div>
                  <div className="detail-item">
                    <label>IP Address:</label>
                    <span>{selectedEntry.ipAddress || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {selectedEntry.license && (
                <div className="detail-section">
                  <h4>License Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>License ID:</label>
                      <span>{selectedEntry.license.licenseId}</span>
                    </div>
                    <div className="detail-item">
                      <label>Company:</label>
                      <span>{selectedEntry.license.company || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Expiry Date:</label>
                      <span>{selectedEntry.license.expiryDate || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Number of Stores:</label>
                      <span>{selectedEntry.license.numOfStores || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Server Type:</label>
                      <span>{selectedEntry.license.serverType || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Layout Designer:</label>
                      <span>{selectedEntry.license.layoutDesigner || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Payment Type:</label>
                      <span>{selectedEntry.license.paymentLicenseType || 'N/A'}</span>
                    </div>
                  </div>
                  {selectedEntry.license.signedLicense && (
                    <div className="detail-item full-width">
                      <label>Signed License:</label>
                      <textarea
                        readOnly
                        value={selectedEntry.license.signedLicense}
                        rows="4"
                        className="signed-license-display"
                      />
                    </div>
                  )}
                </div>
              )}

              {selectedEntry.redmineTicket && (
                <div className="detail-section">
                  <h4>Redmine Ticket</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Ticket ID:</label>
                      <a
                        href={`https://redmine.solumesl.com/issues/${selectedEntry.redmineTicket.ticketId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{selectedEntry.redmineTicket.ticketId}
                      </a>
                    </div>
                    <div className="detail-item">
                      <label>Subject:</label>
                      <span>{selectedEntry.redmineTicket.subject || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Project:</label>
                      <span>{selectedEntry.redmineTicket.project || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status:</label>
                      <span>{selectedEntry.redmineTicket.status || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedEntry.details && Object.keys(selectedEntry.details).length > 0 && (
                <div className="detail-section">
                  <h4>Additional Details</h4>
                  <div className="detail-grid">
                    {selectedEntry.details.notes && (
                      <div className="detail-item full-width">
                        <label>Notes:</label>
                        <span>{selectedEntry.details.notes}</span>
                      </div>
                    )}
                    {selectedEntry.details.error && (
                      <div className="detail-item full-width error-detail">
                        <label>Error:</label>
                        <span>{selectedEntry.details.error}</span>
                      </div>
                    )}
                    {selectedEntry.details.sharepointItemId && (
                      <div className="detail-item">
                        <label>SharePoint Item ID:</label>
                        <span>{selectedEntry.details.sharepointItemId}</span>
                      </div>
                    )}
                    {selectedEntry.details.ticketClosed !== undefined && (
                      <div className="detail-item">
                        <label>Ticket Closed:</label>
                        <span>{selectedEntry.details.ticketClosed ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseHistory;
